import Component from '@bythepixel/component-loader';
import Breakpoints from '@Front/constants/breakpoints';
import Animations from '@Front/animations';

const cssClasses = {
  capabilityCard: '.capability-card',
  gradientEl: '.capability-card__gradient',
};

export default class HomepageController extends Component {
  static selector = '[data-comp-homepage-controller]';
  static loaderPriority = 'in-view';

  constructor(containerEl, options = cssClasses) {
    super(containerEl, options);
    this.scrollDirection = null;
    this.capabilityCardsContainer = this.$container.querySelector('[data-comp-homepage-capability-cards]');
    this.capabilityCards = this.capabilityCardsContainer.querySelectorAll(options.capabilityCard);
    this.mount();
  }

  hideCardGradients() {
    this.capabilityCards.forEach((card) => {
      const gradientBg = this.getCardGradient(card);
      gradientBg.classList.remove('active');
    });
  }

  revealCardGradient(cardContainer) {
    const gradientBg = this.getCardGradient(cardContainer);
    gradientBg.classList.add('active');
  }

  toggleCardGradient(cardContainer) {
    const gradientBg = this.getCardGradient(cardContainer);
    gradientBg.classList.toggle('active');
  }

  getCardGradient(cardContainer) {
    return cardContainer.querySelector(this.$options.gradientEl);
  }

  attachScrollListener() {
    let prevScroll = window.scrollY;

    window.addEventListener('scroll', () => {
      const scroll = window.scrollY;
      this.scrollDirection = (scroll - prevScroll) < 0 ? 'up' : 'down';
      prevScroll = scroll;
    });
  }

  mount() {
    this.animateTopElements();
    this.attachCredibilityObserver();
    this.attachCapabilitiesContentObserver();
    this.attachCapabilitiesCardsObserver();

    if (this.$container.querySelector('.homepage__featured-studies')) {
      this.attachFeaturedStudiesObserver();
    }
  }

  attachCapabilitiesCardsObserver() {
    const options = {
      rootMargin: '0px 0px -50% 0px',
      threshold: 0.5,
    };

    const elements = this.$container.querySelectorAll('.capability-card');

    const clearGradients = () => {
      elements.forEach((element) => {
        const gradient = element.querySelector('.capability-card__gradient');
        gradient.classList.remove('active');
      });
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const { isIntersecting, intersectionRatio, target } = entry;
        const gradient = target.querySelector('.capability-card__gradient');

        if (!isIntersecting) return;

        if (intersectionRatio >= 0.5) {
          clearGradients();
          gradient.classList.add('active');
        }
      });
    }, options);

    elements.forEach(element => observer.observe(element));
  }

  attachCapabilitiesContentObserver() {
    const rootMarginMap = {
      desktop: '0px 0px -30% 0px',
      mobile: '0px',
    };

    const { innerWidth } = window;
    const rootMargin = innerWidth < Breakpoints.desktop ? rootMarginMap.mobile : rootMarginMap.desktop;

    const options = {
      rootMargin,
      threshold: 1,
    };

    const elements = [
      this.$container.querySelector('.sticky-content__title'),
      this.$container.querySelector('.sticky-content__body'),
      this.$container.querySelector('.sticky-content__cta'),
    ];

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        const isCtaBlock = el.classList.contains('sticky-content__cta');

        if (!entry.isIntersecting) return;

        if (el.dataset.animated !== 'true') {
          if (isCtaBlock) {
            const div = el.querySelector('div');
            Animations.maskReveal(div, { shiftDirection: 'up' });
          } else {
            Animations.maskRevealText(el, { revealDirection: 'up' });
          }

          el.dataset.animated = 'true';
        }
      });
    }, options);

    elements.forEach(element => observer.observe(element));
  }

  attachCredibilityObserver() {
    const options = {
      rootMargin: '0px',
      threshold: 0.2,
    };

    const elements = [
      this.$container.querySelector('.credibility-section__header-title'),
      this.$container.querySelector('.credibility-section__header-body'),
      this.$container.querySelector('.credibility-section__header-ratings'),
    ];

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        const isRatingsBlock = el.classList.contains('credibility-section__header-ratings');

        if (!entry.isIntersecting) return;

        if (el.dataset.animated !== 'true') {
          if (isRatingsBlock) {
            const ratings = el.querySelectorAll('.credibility-section__header-rating');
            ratings.forEach((rating) => {
              Animations.maskReveal(rating);
            });
          } else {
            Animations.maskRevealText(el);
          }

          el.dataset.animated = 'true';
        }
      });
    }, options);

    elements.forEach(element => observer.observe(element));
  }

  attachFeaturedStudiesObserver() {
    const options = {
      rootMargin: '0px',
      threshold: 0.1,
    };

    const elements = [
      this.$container.querySelector('.featured-studies__header-title'),
      this.$container.querySelector('.featured-studies__header-actions'),
    ];

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const el = entry.target;
        const isActionsBlock = el.classList.contains('featured-studies__header-actions');

        if (!entry.isIntersecting) return;

        if (el.dataset.animated !== 'true') {
          if (isActionsBlock) {
            const cta = el.querySelector('div');
            Animations.maskReveal(cta);
          } else {
            Animations.maskRevealText(el);
          }

          el.dataset.animated = 'true';
        }
      });
    }, options);

    elements.forEach(element => observer.observe(element));
  }

  attachCapabilityCardObservers() {
    const options = {
      rootMargin: '0px',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          Animations.maskReveal(entry.target, {
            delay: 0,
            yStart: -600,
          });
        }
      });
    }, options);

    this.capabilityCards.forEach(card => observer.observe(card));
  }

  animateTopElements() {
    const noiseEnd = window.innerWidth <= Breakpoints.tablet ? 63 : 84;

    const elementAnimations = [
      {
        element: document.querySelector('.mobile-nav__activator'),
        animation: 'maskReveal',
        options: {
          delay: 0,
          yStart: 200,
          wrapInMask: false,
        },
      },
      {
        element: document.querySelector('.page-nav__logo-link'),
        animation: 'maskReveal',
        options: {
          delay: 0.25,
          wrapInMask: false,
          yStart: 200,
        },
      },
      {
        element: document.querySelector('.page-nav__links'),
        animation: 'maskReveal',
        options: {
          delay: 0.5,
          wrapInMask: false,
          yStart: 200,
        },
      },
      {
        element: document.querySelector('.homepage__hero-noise'),
        animation: 'maskReveal',
        options: {
          delay: 0,
          duration: 1,
          yStart: 2000,
          yEnd: noiseEnd,
          ease: 'power2',
        },
      },
    ];

    elementAnimations.forEach((item) => {
      Animations[item.animation](item.element, item.options);
    });
  }
}
